import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Mail, KeyRound, ArrowRight, Loader2, 
  ShieldCheck, AlertCircle, Lock 
} from 'lucide-react';

const LoginPage = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState(1); // 1=email, 2=otp
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);

  const [formData, setFormData] = useState({
    email: '',
    otp: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  /* ================= SEND OTP ================= */
  const sendOtp = async (e) => {
    if(e) e.preventDefault(); // Prevent form reload
    setLoading(true);
    setError('');

    try {
      const res = await fetch('https://api-dg3-core-inv5.adminpaymentdailygo.co.in/api/admin/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      setStep(2);
      startResendTimer();
    } catch (err) {
      setError(err.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  /* ================= VERIFY OTP ================= */
  const verifyOtp = async (e) => {
    if(e) e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch('https://api-dg3-core-inv5.adminpaymentdailygo.co.in/api/admin/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          otp: formData.otp,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      localStorage.setItem('isAdminAuthenticated', 'true');
      localStorage.setItem('adminEmail', formData.email);

      navigate('/admin/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  /* ================= RESEND TIMER ================= */
  const startResendTimer = () => {
    setResendTimer(30);
  };

  useEffect(() => {
    if (resendTimer > 0) {
      const interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [resendTimer]);

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center bg-slate-950 overflow-hidden font-sans">
      
      {/* Background Decor: Grid & Glows */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-blue-500/10 blur-[100px] rounded-full mix-blend-screen" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-indigo-500/10 blur-[100px] rounded-full mix-blend-screen" />
      </div>

      <div className="relative z-10 w-full max-w-md p-6">
        
        {/* Brand / Logo Area */}
        <div className="flex flex-col items-center mb-8">
          <div className="bg-slate-900 p-3 rounded-2xl border border-slate-800 shadow-xl mb-4 relative group">
            <div className="absolute inset-0 bg-blue-500/20 blur-xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <ShieldCheck size={32} className="text-blue-500 relative z-10" />
          </div>
          <h2 className="text-3xl font-bold text-white tracking-tight">
            Admin Portal
          </h2>
          <p className="text-slate-400 text-sm mt-2">
            Secure access for authorized personnel
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-slate-900/40 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl ring-1 ring-white/5">
          
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-1">
              {step === 1 ? 'Welcome Back' : 'Two-Factor Auth'}
            </h3>
            <p className="text-xs text-slate-400">
              {step === 1
                ? 'Please enter your registered email to continue.'
                : `Enter the code sent to ${formData.email}`}
            </p>
          </div>

          {error && (
            <div className="mb-6 flex items-start gap-3 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-xs animate-in fade-in slide-in-from-top-2">
              <AlertCircle size={16} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {step === 1 ? (
            <form onSubmit={sendOtp} className="space-y-5">
              <Input
                icon={Mail}
                name="email"
                type="email"
                placeholder="admin@company.com"
                value={formData.email}
                onChange={handleChange}
                autoFocus
              />

              <Button loading={loading}>
                Continue <ArrowRight size={18} />
              </Button>
            </form>
          ) : (
            <form onSubmit={verifyOtp} className="space-y-6">
              <div className="space-y-2">
                <Input
                  icon={KeyRound}
                  name="otp"
                  placeholder="0 0 0 0"
                  value={formData.otp}
                  onChange={handleChange}
                  maxLength={4}
                  className="text-center tracking-[1em] font-mono text-lg placeholder:font-sans placeholder:tracking-normal placeholder:text-sm"
                />
              </div>

              <Button loading={loading} variant="success">
                Verify Access
              </Button>

              <div className="flex items-center justify-between text-xs mt-4 pt-4 border-t border-slate-800">
                <button 
                  type="button" 
                  onClick={() => { setStep(1); setError(''); }}
                  className="text-slate-500 hover:text-white transition-colors"
                >
                  Change Email
                </button>
                
                {resendTimer > 0 ? (
                  <span className="text-slate-500 font-mono">
                    Resend in 00:{resendTimer.toString().padStart(2, '0')}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={sendOtp}
                    className="text-blue-400 hover:text-blue-300 font-medium transition-colors"
                  >
                    Resend Code
                  </button>
                )}
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center flex items-center justify-center gap-2 text-[10px] text-slate-600">
          <Lock size={10} />
          <span>Encrypted Connection • IP Logged</span>
        </div>
      </div>
    </div>
  );
};

/* ================= COMPONENT: INPUT ================= */
const Input = ({ icon: Icon, className = "", ...props }) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <div className={`relative group transition-all duration-300 ${isFocused ? 'scale-[1.01]' : ''}`}>
      <div className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors duration-300 ${isFocused ? 'text-blue-400' : 'text-slate-500'}`}>
        <Icon size={18} />
      </div>
      <input
        {...props}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        className={`w-full pl-11 pr-4 py-3.5 rounded-xl bg-slate-950/50 border border-slate-800 text-white placeholder:text-slate-600 outline-none transition-all duration-300
          focus:border-blue-500/50 focus:bg-slate-900 focus:shadow-[0_0_15px_rgba(59,130,246,0.1)]
          hover:border-slate-700 ${className}`}
      />
    </div>
  );
};

/* ================= COMPONENT: BUTTON ================= */
const Button = ({ children, loading, variant = "primary" }) => {
  const baseStyles = "w-full py-3.5 rounded-xl font-medium text-sm flex items-center justify-center gap-2 transition-all duration-300 transform active:scale-[0.98] shadow-lg";
  
  const variants = {
    primary: "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white shadow-blue-900/20",
    success: "bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white shadow-emerald-900/20"
  };

  return (
    <button
      disabled={loading}
      className={`${baseStyles} ${variants[variant]} ${loading ? "opacity-80 cursor-wait" : ""}`}
    >
      {loading ? <Loader2 className="animate-spin" size={18} /> : children}
    </button>
  );
};

export default LoginPage;